import { useRef, useState } from 'react';
import { z } from 'zod';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ELECTRONIC_INVOICE_FORMATS,
  compareDecimal,
  electronicInvoiceCommitSchema,
  electronicInvoicePrepaymentReviewSchema,
  electronicInvoiceSettingsSchema,
  type ElectronicInvoiceImport,
  type ElectronicInvoiceSettings,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePermissions } from '@/features/auth';
import { useFeatureAvailability } from '@/features/platform-features';
import { downloadElectronicInvoice, useElectronicInvoiceRetryKey } from '../electronic-invoice-ui';
import {
  useElectronicInvoiceLookups,
  useElectronicInvoiceMutation,
  useElectronicInvoiceParameters,
  useElectronicInvoiceSettings,
  useElectronicInvoiceSetup,
} from '../api/use-electronic-invoices';

const selectClass = 'min-h-11 w-full rounded-md border border-input bg-background px-3 text-sm';
export function ElectronicInvoiceError({ message }: { message: string }) {
  const { t } = useTranslation();
  return message ? (
    <p role="alert" className="rounded-md border border-destructive p-3 text-destructive">
      {t(message)}
    </p>
  ) : null;
}

const parametersForm = z.object({
  buyerReference: z.string().max(200),
  endpoint: z.string().max(200),
  scheme: z.string(),
  orderReference: z.string().max(200),
  receiptReference: z.string().max(200),
  exemptionReason: z.string().max(2000),
});
export function ElectronicInvoiceDocumentPanel({
  documentId,
  locked,
  customerId,
}: {
  documentId: string;
  locked: boolean;
  customerId?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const features = useFeatureAvailability();
  const query = useElectronicInvoiceParameters(documentId);
  const settings = useElectronicInvoiceSettings();
  const mutation = useElectronicInvoiceMutation();
  const key = useElectronicInvoiceRetryKey();
  const [error, setError] = useState('');
  const [selectedFormat, setFormat] = useState<
    'FACTUR_X' | 'XRECHNUNG_CII' | 'XRECHNUNG_UBL' | null
  >(null);
  const preferred = customerId
    ? (settings.data?.customerFormats[customerId] ?? settings.data?.defaultFormat)
    : settings.data?.defaultFormat;
  const enabledFormats = settings.data?.enabledFormats ?? ELECTRONIC_INVOICE_FORMATS;
  const format =
    selectedFormat && enabledFormats.includes(selectedFormat)
      ? selectedFormat
      : preferred && preferred !== 'NONE' && enabledFormats.includes(preferred)
        ? preferred
        : (enabledFormats[0] ?? 'FACTUR_X');
  const form = useForm<z.infer<typeof parametersForm>>({
    resolver: zodResolver(parametersForm),
    values: {
      buyerReference: query.data?.parameters.buyerReference ?? '',
      endpoint: query.data?.parameters.buyerEndpoint?.value ?? '',
      scheme: query.data?.parameters.buyerEndpoint?.scheme ?? 'EM',
      orderReference: query.data?.parameters.orderReference ?? '',
      receiptReference: query.data?.parameters.receiptReference ?? '',
      exemptionReason: query.data?.parameters.exemptionReason ?? '',
    },
  });
  if (!features.isEnabled('electronic_invoices') || !can('einvoice:read')) return <></>;
  return (
    <section className="space-y-3 rounded-md border p-4">
      <h2 className="font-semibold">{t('electronicInvoices.title')}</h2>
      <p className="text-sm text-muted-foreground">
        {t(
          preferred === 'NONE'
            ? 'electronicInvoices.none'
            : locked
              ? 'electronicInvoices.frozen'
              : 'electronicInvoices.finalizeHint',
        )}
      </p>
      <ElectronicInvoiceError message={error} />
      {query.isPending ? (
        <p role="status">{t('electronicInvoices.loading')}</p>
      ) : query.isError ? (
        <Button onClick={() => void query.refetch()}>{t('electronicInvoices.retry')}</Button>
      ) : (
        <form
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={form.handleSubmit(async (values) => {
            setError('');
            const input = {
              version: query.data?.version ?? 0,
              parameters: {
                buyerReference: values.buyerReference || null,
                buyerEndpoint: values.endpoint
                  ? { value: values.endpoint, scheme: values.scheme }
                  : null,
                orderReference: values.orderReference || null,
                receiptReference: values.receiptReference || null,
                exemptionReason: values.exemptionReason || null,
              },
            };
            try {
              await mutation.mutateAsync({
                action: 'parameters',
                documentId,
                input,
                key: key(input),
              });
            } catch (failure) {
              setError(failure instanceof Error ? failure.message : 'electronicInvoices.error');
            }
          })}
        >
          {(
            [
              'buyerReference',
              'endpoint',
              'orderReference',
              'receiptReference',
              'exemptionReason',
            ] as const
          ).map((name) => (
            <div key={name}>
              <Label htmlFor={`einvoice-${name}`}>{t(`electronicInvoices.${name}`)}</Label>
              <Input
                id={`einvoice-${name}`}
                {...form.register(name)}
                readOnly={locked || !can('einvoice:export')}
                aria-invalid={!!form.formState.errors[name]}
              />
              {form.formState.errors[name] && (
                <p role="alert">{t('electronicInvoices.invalidInvoice')}</p>
              )}
            </div>
          ))}
          <div>
            <Label htmlFor="einvoice-scheme">{t('electronicInvoices.endpointType')}</Label>
            <select
              id="einvoice-scheme"
              className={selectClass}
              {...form.register('scheme')}
              disabled={locked || !can('einvoice:export')}
            >
              <option value="EM">{t('electronicInvoices.email')}</option>
              <option value="0088">GLN</option>
              <option value="0204">Leitweg-ID</option>
            </select>
          </div>
          {!locked && can('einvoice:export') && (
            <Button type="submit" disabled={mutation.isPending}>
              {t('electronicInvoices.save')}
            </Button>
          )}
        </form>
      )}
      {locked && preferred !== 'NONE' && can('einvoice:export') && (
        <div className="flex flex-wrap gap-2">
          <Label htmlFor="einvoice-format" className="sr-only">
            {t('electronicInvoices.format')}
          </Label>
          <select
            id="einvoice-format"
            className={selectClass}
            value={format}
            onChange={(event) =>
              setFormat(
                ELECTRONIC_INVOICE_FORMATS.find((entry) => entry === event.target.value) ??
                  'FACTUR_X',
              )
            }
          >
            {enabledFormats.map((entry) => (
              <option value={entry} key={entry}>
                {t(`electronicInvoices.formats.${entry}`)}
              </option>
            ))}
          </select>
          <Button
            disabled={mutation.isPending}
            onClick={async () => {
              setError('');
              try {
                const result = await mutation.mutateAsync({
                  action: 'export',
                  documentId,
                  format,
                  key: key({ documentId, format }),
                });
                if ('downloadUrl' in result)
                  await downloadElectronicInvoice(result.downloadUrl, 'invoice');
              } catch (failure) {
                setError(failure instanceof Error ? failure.message : 'electronicInvoices.error');
              }
            }}
          >
            {t(mutation.isPending ? 'electronicInvoices.validating' : 'electronicInvoices.export')}
          </Button>
        </div>
      )}
    </section>
  );
}

export function ElectronicInvoiceImportPreview({
  preview,
  completed,
}: {
  preview: ElectronicInvoiceImport;
  completed: (value: ElectronicInvoiceImport) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { can } = usePermissions();
  const availability = useFeatureAvailability();
  const mutation = useElectronicInvoiceMutation();
  const key = useElectronicInvoiceRetryKey();
  const [error, setError] = useState('');
  const form = useForm<{
    supplierId: string;
    purchaseOrderId: string;
    vendorInvoiceId: string;
    lineMappings: { lineId: string; purchaseOrderItemId: string; goodsReceiptItemId: string }[];
    prepaymentAllocations: { paymentId: string; amount: string }[];
  }>({
    defaultValues: {
      supplierId: '',
      purchaseOrderId: '',
      vendorInvoiceId: '',
      prepaymentAllocations: [],
      lineMappings: preview.invoice.lines.map((line) => ({
        lineId: line.id,
        purchaseOrderItemId: '',
        goodsReceiptItemId: '',
      })),
    },
  });
  const advances = useFieldArray({ control: form.control, name: 'prepaymentAllocations' });
  const lookups = useElectronicInvoiceLookups(
    form.watch('supplierId') || preview.supplierId || undefined,
  );
  const prepaymentMode =
    lookups.isSuccess && !lookups.isFetching ? lookups.data.prepaymentMode : undefined;
  const currentPrepaymentMode = useRef(prepaymentMode);
  currentPrepaymentMode.current = prepaymentMode;
  const hasPrepaid = compareDecimal(preview.invoice.totals.prepaid, '0') > 0;
  const lookupStatus =
    lookups.isPending || lookups.isFetching ? (
      hasPrepaid ? (
        <p role="status">{t('electronicInvoices.loading')}</p>
      ) : null
    ) : !prepaymentMode ? (
      <div className="space-y-2">
        <ElectronicInvoiceError message="electronicInvoices.error" />
        <Button type="button" variant="outline" onClick={() => void lookups.refetch()}>
          {t('electronicInvoices.retry')}
        </Button>
      </div>
    ) : null;
  const nativeAdvanceGuidance = hasPrepaid && prepaymentMode === 'NATIVE_LEDGER' && (
    <div className="space-y-2 rounded-md border p-3">
      <p role="status">{t('electronicInvoices.nativePrepaymentNotice')}</p>
      {!can('purchase:read') && <p>{t('electronicInvoices.nativePrepaymentPermission')}</p>}
    </div>
  );
  const order = lookups.data?.purchaseOrders.find(
    (entry) => entry.id === form.watch('purchaseOrderId'),
  );
  const money = (value: string) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: preview.invoice.currency,
    }).format(Number(value));
  const existingDraft = lookups.data?.vendorDrafts.find(
    (entry) => entry.id === form.watch('vendorInvoiceId'),
  );
  const advanceFields = hasPrepaid && prepaymentMode === 'LEGACY' && (
    <fieldset className="space-y-3 rounded-md border p-3">
      <legend className="px-1 font-medium">{t('electronicInvoices.prepaid')}</legend>
      <p className="text-sm text-muted-foreground">{t('electronicInvoices.prepaymentNotice')}</p>
      {can('banking:read') && can('banking:post') ? (
        <>
          {advances.fields.map((advance, index) => (
            <div key={advance.id} className="grid gap-2 sm:grid-cols-[1fr_10rem_auto]">
              <Label className="sr-only" htmlFor={`einvoice-advance-${index}`}>
                {t('electronicInvoices.advancePayment')}
              </Label>
              <select
                id={`einvoice-advance-${index}`}
                className={selectClass}
                {...form.register(`prepaymentAllocations.${index}.paymentId`)}
                onChange={(event) => {
                  form.setValue(`prepaymentAllocations.${index}.paymentId`, event.target.value, {
                    shouldDirty: true,
                  });
                  const payment = lookups.data?.vendorAdvances.find(
                    (entry) =>
                      entry.id === event.target.value &&
                      entry.currency === preview.invoice.currency,
                  );
                  if (payment)
                    form.setValue(
                      `prepaymentAllocations.${index}.amount`,
                      compareDecimal(payment.unallocatedAmount, preview.invoice.totals.prepaid) < 0
                        ? payment.unallocatedAmount
                        : preview.invoice.totals.prepaid,
                      { shouldDirty: true },
                    );
                }}
              >
                <option value="">{t('electronicInvoices.select')}</option>
                {lookups.data?.vendorAdvances
                  .filter((entry) => entry.currency === preview.invoice.currency)
                  .map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.bookingDate} ·{' '}
                      {entry.reference ?? t('electronicInvoices.advancePayment')} ·{' '}
                      {money(entry.unallocatedAmount)}
                    </option>
                  ))}
              </select>
              <Label className="sr-only" htmlFor={`einvoice-advance-amount-${index}`}>
                {t('electronicInvoices.advanceAmount')}
              </Label>
              <Input
                id={`einvoice-advance-amount-${index}`}
                inputMode="decimal"
                {...form.register(`prepaymentAllocations.${index}.amount`)}
              />
              <Button variant="outline" type="button" onClick={() => advances.remove(index)}>
                {t('electronicInvoices.removeAdvance')}
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            type="button"
            disabled={
              (!form.watch('supplierId') && !preview.supplierId) ||
              !lookups.data?.vendorAdvances.some(
                (entry) => entry.currency === preview.invoice.currency,
              )
            }
            onClick={() =>
              advances.append({ paymentId: '', amount: preview.invoice.totals.prepaid })
            }
          >
            {t('electronicInvoices.linkAdvance')}
          </Button>
        </>
      ) : (
        <p role="status">{t('electronicInvoices.prepaymentPermission')}</p>
      )}
    </fieldset>
  );
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">
        {preview.invoice.number} · {preview.invoice.seller.name}
      </h2>
      <p>{t('electronicInvoices.reviewNotice')}</p>
      {preview.invoice.kind === 'CREDIT_NOTE' && (
        <p role="status" className="rounded-md border p-3">
          {t('electronicInvoices.supplierCreditReviewRequired')}
        </p>
      )}
      <ElectronicInvoiceError message={error} />
      {lookupStatus}
      {nativeAdvanceGuidance}
      <dl className="grid grid-cols-2 gap-2 text-sm">
        <dt>{t('electronicInvoices.net')}</dt>
        <dd>{money(preview.invoice.totals.net)}</dd>
        <dt>{t('electronicInvoices.tax')}</dt>
        <dd>{money(preview.invoice.totals.tax)}</dd>
        <dt>{t('electronicInvoices.gross')}</dt>
        <dd>{money(preview.invoice.totals.gross)}</dd>
        <dt>{t('electronicInvoices.allowances')}</dt>
        <dd>{money(preview.invoice.totals.allowances)}</dd>
        <dt>{t('electronicInvoices.charges')}</dt>
        <dd>{money(preview.invoice.totals.charges)}</dd>
        {(Number(preview.invoice.totals.prepaid) !== 0 ||
          Number(preview.invoice.totals.rounding) !== 0) && (
          <>
            <dt>{t('electronicInvoices.prepaid')}</dt>
            <dd>{money(preview.invoice.totals.prepaid)}</dd>
            <dt>{t('electronicInvoices.rounding')}</dt>
            <dd>{money(preview.invoice.totals.rounding)}</dd>
            <dt>{t('electronicInvoices.due')}</dt>
            <dd>{money(preview.invoice.totals.due)}</dd>
          </>
        )}
        <dt>{t('electronicInvoices.validation')}</dt>
        <dd>
          {t(
            preview.validation.accepted
              ? 'electronicInvoices.valid'
              : 'electronicInvoices.invalidInvoice',
          )}
        </dd>
      </dl>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() =>
            void downloadElectronicInvoice(
              `/api/v1/electronic-invoices/imports/${preview.id}/original`,
              preview.fileName,
            ).catch((failure: unknown) =>
              setError(failure instanceof Error ? failure.message : 'electronicInvoices.error'),
            )
          }
        >
          {t('electronicInvoices.original')}
        </Button>
        <Button
          variant="outline"
          onClick={() =>
            void downloadElectronicInvoice(
              `/api/v1/electronic-invoices/imports/${preview.id}/reports`,
              'validation.json',
            ).catch((failure: unknown) =>
              setError(failure instanceof Error ? failure.message : 'electronicInvoices.error'),
            )
          }
        >
          {t('electronicInvoices.reports')}
        </Button>
      </div>
      {preview.validation.issues.map((issue, index) => (
        <p key={index} className="text-sm">
          {issue.rule}: {t(issue.message)}
        </p>
      ))}
      {preview.vendorInvoiceId ? (
        <div className="space-y-3">
          {can('purchase:read') && (
            <Link
              className="text-primary underline"
              to={`/procurement/vendor-invoices/${preview.vendorInvoiceId}/match`}
            >
              {t('electronicInvoices.openDraft')}
            </Link>
          )}
          {hasPrepaid &&
            prepaymentMode === 'LEGACY' &&
            can('einvoice:import') &&
            can('banking:post') &&
            availability.isEnabled('electronic_invoices') && (
              <form
                className="space-y-3"
                onSubmit={form.handleSubmit(async (values) => {
                  setError('');
                  if (currentPrepaymentMode.current !== 'LEGACY') return;
                  try {
                    const input = electronicInvoicePrepaymentReviewSchema.parse({
                      version: preview.version,
                      previewHash: preview.previewHash,
                      vendorInvoiceVersion: preview.vendorInvoiceVersion,
                      prepaymentAllocations: values.prepaymentAllocations,
                    });
                    const result = await mutation.mutateAsync({
                      action: 'prepayments',
                      id: preview.id,
                      input,
                      key: key(input),
                    });
                    if ('previewHash' in result) {
                      advances.replace([]);
                      completed(result);
                    }
                  } catch (failure) {
                    setError(
                      failure instanceof z.ZodError
                        ? 'electronicInvoices.prepaymentInvalid'
                        : failure instanceof Error
                          ? failure.message
                          : 'electronicInvoices.error',
                    );
                  }
                })}
              >
                {advanceFields}
                <Button
                  type="submit"
                  disabled={
                    mutation.isPending ||
                    advances.fields.length === 0 ||
                    preview.vendorInvoiceVersion === null
                  }
                >
                  {t('electronicInvoices.reconcileAdvances')}
                </Button>
              </form>
            )}
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit(async (values) => {
            setError('');
            const mode = currentPrepaymentMode.current;
            if (!mode) {
              setError('electronicInvoices.error');
              return;
            }
            try {
              const input = electronicInvoiceCommitSchema.parse({
                version: preview.version,
                previewHash: preview.previewHash,
                supplierId: values.supplierId,
                purchaseOrderId: values.purchaseOrderId || null,
                vendorInvoiceId: values.vendorInvoiceId || null,
                ...(existingDraft ? { vendorInvoiceVersion: existingDraft.version } : {}),
                prepaymentAllocations: mode === 'LEGACY' ? values.prepaymentAllocations : [],
                lineMappings: values.lineMappings.map((line) => ({
                  ...line,
                  purchaseOrderItemId: line.purchaseOrderItemId || null,
                  goodsReceiptItemId: line.goodsReceiptItemId || null,
                })),
              });
              const result = await mutation.mutateAsync({
                action: 'commit',
                id: preview.id,
                input,
                key: key(input),
              });
              if ('previewHash' in result) completed(result);
            } catch (failure) {
              setError(
                failure instanceof z.ZodError
                  ? failure.issues.some((issue) => issue.path[0] === 'prepaymentAllocations')
                    ? 'electronicInvoices.prepaymentInvalid'
                    : 'electronicInvoices.supplierRequired'
                  : failure instanceof Error
                    ? failure.message
                    : 'electronicInvoices.error',
              );
            }
          })}
        >
          {preview.duplicateVendorInvoiceId && (
            <p role="alert">{t('electronicInvoices.duplicateInvoice')}</p>
          )}
          {preview.suppliers.length === 0 && (
            <ElectronicInvoiceError message="electronicInvoices.supplierMissing" />
          )}
          <Label htmlFor="einvoice-supplier">{t('electronicInvoices.supplier')}</Label>
          <select
            id="einvoice-supplier"
            className={selectClass}
            {...form.register('supplierId')}
            onChange={(event) => {
              form.setValue('supplierId', event.target.value);
              form.setValue('purchaseOrderId', '');
              form.setValue('vendorInvoiceId', '');
              advances.replace([]);
              form.setValue(
                'lineMappings',
                preview.invoice.lines.map((line) => ({
                  lineId: line.id,
                  purchaseOrderItemId: '',
                  goodsReceiptItemId: '',
                })),
              );
            }}
          >
            <option value="">{t('electronicInvoices.select')}</option>
            {preview.suppliers.map((supplier) => (
              <option value={supplier.id} key={supplier.id}>
                {supplier.name} · {supplier.vatId}
              </option>
            ))}
          </select>
          {advanceFields}
          <Label htmlFor="einvoice-existing-draft">{t('electronicInvoices.targetDraft')}</Label>
          <select
            id="einvoice-existing-draft"
            className={selectClass}
            {...form.register('vendorInvoiceId')}
          >
            <option value="">{t('electronicInvoices.newDraft')}</option>
            {lookups.data?.vendorDrafts.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.number}
              </option>
            ))}
          </select>
          <Label htmlFor="einvoice-order">{t('electronicInvoices.purchaseOrder')}</Label>
          <select id="einvoice-order" className={selectClass} {...form.register('purchaseOrderId')}>
            <option value="">{t('electronicInvoices.noMapping')}</option>
            {lookups.data?.purchaseOrders.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.number}
              </option>
            ))}
          </select>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="p-2 text-left">{t('electronicInvoices.line')}</th>
                  <th className="p-2 text-left">{t('electronicInvoices.purchaseOrder')}</th>
                  <th className="p-2 text-left">{t('electronicInvoices.receipt')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.invoice.lines.map((line, index) => (
                  <tr key={line.id} className="border-t">
                    <td className="p-2">
                      {line.name} · {line.quantity} {line.unit} · {money(line.net)}
                    </td>
                    <td className="p-2">
                      <select
                        className={selectClass}
                        aria-label={`${t('electronicInvoices.purchaseOrder')} ${line.name}`}
                        {...form.register(`lineMappings.${index}.purchaseOrderItemId`)}
                      >
                        <option value="">{t('electronicInvoices.noMapping')}</option>
                        {(order ? [order] : (lookups.data?.purchaseOrders ?? [])).flatMap((entry) =>
                          entry.items.map((item) => (
                            <option value={item.id} key={item.id}>
                              {entry.number} · {item.name} · {item.quantity}
                            </option>
                          )),
                        )}
                      </select>
                    </td>
                    <td className="p-2">
                      <select
                        className={selectClass}
                        aria-label={`${t('electronicInvoices.receipt')} ${line.name}`}
                        {...form.register(`lineMappings.${index}.goodsReceiptItemId`)}
                      >
                        <option value="">{t('electronicInvoices.noMapping')}</option>
                        {lookups.data?.receipts.flatMap((entry) =>
                          entry.items
                            .filter(
                              (item) =>
                                !form.watch(`lineMappings.${index}.purchaseOrderItemId`) ||
                                item.purchaseOrderItemId ===
                                  form.watch(`lineMappings.${index}.purchaseOrderItemId`),
                            )
                            .map((item) => (
                              <option value={item.id} key={item.id}>
                                {entry.number} · {item.name} · {item.quantity}
                              </option>
                            )),
                        )}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {can('einvoice:import') && availability.isEnabled('electronic_invoices') && (
            <Button
              type="submit"
              disabled={
                mutation.isPending ||
                !form.watch('supplierId') ||
                !prepaymentMode ||
                (preview.duplicateVendorInvoiceId !== null && !existingDraft)
              }
            >
              {t(
                mutation.isPending
                  ? 'electronicInvoices.loading'
                  : 'electronicInvoices.createDraft',
              )}
            </Button>
          )}
        </form>
      )}
    </section>
  );
}

export function ElectronicInvoiceCustomerSettings({
  customerId,
}: {
  customerId: string;
}): JSX.Element {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const query = useElectronicInvoiceSettings();
  const mutation = useElectronicInvoiceMutation();
  const retry = useElectronicInvoiceRetryKey();
  const [error, setError] = useState('');
  if (!can('einvoice:settings')) return <></>;
  return (
    <section className="space-y-2">
      <Label htmlFor={`einvoice-customer-format-${customerId}`}>
        {t('electronicInvoices.customerFormat')}
      </Label>
      <select
        id={`einvoice-customer-format-${customerId}`}
        className={selectClass}
        disabled={query.isPending || mutation.isPending}
        value={query.data?.customerFormats[customerId] ?? 'DEFAULT'}
        onChange={async (event) => {
          if (!query.data) return;
          const customerFormats = { ...query.data.customerFormats };
          const selected = event.target.value;
          if (selected === 'DEFAULT') delete customerFormats[customerId];
          else if (
            selected === 'NONE' ||
            ELECTRONIC_INVOICE_FORMATS.includes(
              selected as (typeof ELECTRONIC_INVOICE_FORMATS)[number],
            )
          )
            customerFormats[customerId] = selected as
              'NONE' | (typeof ELECTRONIC_INVOICE_FORMATS)[number];
          const input = { ...query.data, customerFormats };
          setError('');
          try {
            await mutation.mutateAsync({ action: 'settings', input, key: retry(input) });
          } catch (failure) {
            setError(failure instanceof Error ? failure.message : 'electronicInvoices.error');
          }
        }}
      >
        <option value="DEFAULT">{t('electronicInvoices.defaultFormat')}</option>
        <option value="NONE">{t('electronicInvoices.none')}</option>
        {ELECTRONIC_INVOICE_FORMATS.map((format) => (
          <option value={format} key={format}>
            {t(`electronicInvoices.formats.${format}`)}
          </option>
        ))}
      </select>
      <ElectronicInvoiceError
        message={error || (query.isError ? 'electronicInvoices.error' : '')}
      />
    </section>
  );
}

export function ElectronicInvoiceSettingsPanel(): JSX.Element {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const query = useElectronicInvoiceSettings();
  const setup = useElectronicInvoiceSetup(can('einvoice:settings'));
  const mutation = useElectronicInvoiceMutation();
  const retry = useElectronicInvoiceRetryKey();
  const [error, setError] = useState('');
  const form = useForm<ElectronicInvoiceSettings>({
    resolver: zodResolver(electronicInvoiceSettingsSchema),
    ...(query.data ? { values: query.data } : {}),
  });
  if (!can('einvoice:settings')) return <></>;
  return (
    <section className="space-y-4 rounded-md border p-4">
      <h2 className="text-lg font-semibold">{t('electronicInvoices.settings')}</h2>
      <p>{t('electronicInvoices.setupNotice')}</p>
      <ElectronicInvoiceError
        message={error || (query.isError || setup.isError ? 'electronicInvoices.error' : '')}
      />
      {setup.isPending ? (
        <p role="status">{t('electronicInvoices.loading')}</p>
      ) : (
        <>
          <p>{t(setup.data?.ready ? 'electronicInvoices.ready' : 'electronicInvoices.notReady')}</p>
          <p className="text-sm">
            {setup.data?.facturXVersion} · {setup.data?.xrechnungVersion}
          </p>
          <ul className="text-sm">
            {setup.data?.checks.map((check) => (
              <li key={check.name}>
                {t(`electronicInvoices.checks.${check.name}`)}:{' '}
                {t(check.ready ? 'electronicInvoices.ready' : 'electronicInvoices.notReady')}
              </li>
            ))}
          </ul>
        </>
      )}
      {query.data && (
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit(async (input) => {
            setError('');
            try {
              await mutation.mutateAsync({ action: 'settings', input, key: retry(input) });
            } catch (failure) {
              setError(failure instanceof Error ? failure.message : 'electronicInvoices.error');
            }
          })}
        >
          {ELECTRONIC_INVOICE_FORMATS.map((format) => (
            <label className="flex min-h-11 items-center gap-2" key={format}>
              <input type="checkbox" value={format} {...form.register('enabledFormats')} />
              {t(`electronicInvoices.formats.${format}`)}
            </label>
          ))}
          <Label htmlFor="einvoice-default-format">{t('electronicInvoices.defaultFormat')}</Label>
          <select
            id="einvoice-default-format"
            className={selectClass}
            {...form.register('defaultFormat')}
          >
            {ELECTRONIC_INVOICE_FORMATS.map((format) => (
              <option value={format} key={format}>
                {t(`electronicInvoices.formats.${format}`)}
              </option>
            ))}
          </select>
          <Label htmlFor="einvoice-seller-contact">{t('electronicInvoices.sellerContact')}</Label>
          <Input id="einvoice-seller-contact" {...form.register('sellerContactName')} />
          <Label htmlFor="einvoice-seller-endpoint">{t('electronicInvoices.endpoint')}</Label>
          <Input
            id="einvoice-seller-endpoint"
            value={form.watch('sellerEndpoint')?.value ?? ''}
            onChange={(event) =>
              form.setValue(
                'sellerEndpoint',
                event.target.value
                  ? {
                      value: event.target.value,
                      scheme: form.getValues('sellerEndpoint')?.scheme ?? 'EM',
                    }
                  : null,
              )
            }
          />
          <Label htmlFor="einvoice-seller-scheme">{t('electronicInvoices.endpointType')}</Label>
          <select
            id="einvoice-seller-scheme"
            className={selectClass}
            value={form.watch('sellerEndpoint')?.scheme ?? 'EM'}
            onChange={(event) => {
              const value = form.getValues('sellerEndpoint');
              if (value) form.setValue('sellerEndpoint', { ...value, scheme: event.target.value });
            }}
          >
            <option value="EM">{t('electronicInvoices.email')}</option>
            <option value="0088">GLN</option>
            <option value="0204">Leitweg-ID</option>
          </select>
          <Label htmlFor="einvoice-retention">{t('electronicInvoices.retention')}</Label>
          <Input
            id="einvoice-retention"
            type="number"
            min={10}
            max={30}
            {...form.register('retentionYears', { valueAsNumber: true })}
          />
          {Object.keys(form.formState.errors).length > 0 && (
            <ElectronicInvoiceError message="electronicInvoices.settingsInvalid" />
          )}
          <Button type="submit" disabled={mutation.isPending}>
            {t('electronicInvoices.save')}
          </Button>
        </form>
      )}
      <p className="text-sm text-muted-foreground">{t('electronicInvoices.frenchNotice')}</p>
      <p className="text-sm text-muted-foreground">{t('electronicInvoices.costs')}</p>
      <p className="text-sm text-muted-foreground">{t('electronicInvoices.limits')}</p>
      <ul className="text-sm">
        {setup.data?.sources.map((source) => (
          <li key={source.url}>
            <a
              href={source.url}
              target="_blank"
              rel="noreferrer"
              className="text-primary underline"
            >
              {source.label}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
